import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineSunsetIcon from 'LuxKit/Icons/line/LineSunsetIcon'
import { TOUR_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD } from 'constants/tours'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'

function useTourLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const tourV2BookWithConfidenceItems: Array<App.Tours.V2OfferBookWithConfidenceItem> = [
      {
        title: intl.formatMessage({
          id: 'SKV14Wsziq',
          defaultMessage: 'Book everything in one place',
          description: 'Book with confidence item title for all-in-one booking',
        }),
        description: intl.formatMessage({
          id: '4no5VSRr2n',
          defaultMessage: 'From hotels to flights, experiences and more \u2013 we’ve got your trip covered from start to finish.',
          description: 'Book with confidence item description for all-in-one booking',
        }),
        Icon: LineSunsetIcon,
      },
      {
        title: intl.formatMessage({
          id: 'ChD4Ihr2bH',
          defaultMessage: 'Secure dates with a deposit',
          description: 'Book with confidence item title for deposit booking',
        }),
        description: intl.formatMessage(
          {
            id: 'KynF+LSTV7',
            defaultMessage: 'Reserve your space now with a deposit, pay the rest {dayThreshold, plural, one {# day} other {# days}} before departure',
            description: 'Book with confidence item description for deposit booking with day threshold',
          },
          { dayThreshold: TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD },
        ),
        Icon: LineClockDollarIcon,
      },
      {
        title: intl.formatMessage({
          id: '6i/DoJkixr',
          defaultMessage: '7-day change of mind refund',
          description: 'Book with confidence item title for refund policy',
        }),
        description: intl.formatMessage({
          id: 'mzT50M4qG1',
          defaultMessage: 'Get a full refund on your booking (excludes cruises, flights, travel protection and the service fee paid on deposit bookings)',
          description: 'Book with confidence item description for refund policy exclusions',
        }),
        Icon: LineRefundIcon,
      },
      {
        title: intl.formatMessage({
          id: 'nhmMuEOy3G',
          defaultMessage: 'Handpicked partners',
          description: 'Book with confidence item title for curated travel partners',
        }),
        description: intl.formatMessage({
          id: 'rpZBe4QQmq',
          defaultMessage: 'We work with the world’s most loved brands in travel',
          description: 'Book with confidence item description for curated travel partners',
        }),
        Icon: LineBuildingIcon,
      },
    ]

    const tourDurationOptions: Array<DurationOption> = [
      {
        title: intl.formatMessage({
          id: 'lcYvWNR9q6',
          defaultMessage: 'Any duration',
          description: 'Tour duration option title for any duration',
        }),
        caption: intl.formatMessage({
          id: '3bHufjSHNk',
          defaultMessage: 'I’ll decide later',
          description: 'Tour duration option caption for deciding later',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
      },
      {
        title: intl.formatMessage({
          id: 'EN6Zn03N/g',
          defaultMessage: 'Short tour',
          description: 'Tour duration option title for short tours of 1 to 7 days',
        }),
        caption: intl.formatMessage({
          id: 'kZ7YYlNlcr',
          defaultMessage: '1 to 7 days',
          description: 'Tour duration option caption for short tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.SHORT_STAY,
      },
      {
        title: intl.formatMessage({
          id: 'wT4Xju4Hgn',
          defaultMessage: 'Medium tour',
          description: 'Tour duration option title for medium tours of 8 to 14 days',
        }),
        caption: intl.formatMessage({
          id: 'mzisjga0CO',
          defaultMessage: '8 to 14 days',
          description: 'Tour duration option caption for medium tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY,
      },
      {
        title: intl.formatMessage({
          id: 'PAb4HOU/wf',
          defaultMessage: 'Long tour',
          description: 'Tour duration option title for long tours of 15 or more days',
        }),
        caption: intl.formatMessage({
          id: 'fVqTz+grnw',
          defaultMessage: '15+ days',
          description: 'Tour duration option caption for long tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.LONG_STAY,
      },
    ]

    return {
      tourV2BookWithConfidenceItems,
      tourDurationOptions,
    }
  }, [intl])
}

export default useTourLabels
